import React, { useState, useEffect } from 'react';
// Use global ProjectInterface from vite-env.d.ts which includes owner_id
import { useProjectUsers } from '../../hooks/useProjectUsers';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useUsersStore, useUsersPage } from '../../state/users';
import { AddUserModal } from './AddUserModal';
import { ChangeRoleModal } from './ChangeRoleModal';
import { ChangeEscalationLevelModal } from './ChangeEscalationLevelModal';
import { EditUserModal } from './EditUserModal';
import { ProjectTeamErrorBoundary } from './ProjectTeamErrorBoundary';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../Avatar';
import { useEscalationMatrixStore, useEscalationMatrixPage } from '../../state/escalationMatrix';
import { useKeycloak } from '@react-keycloak/web';
import { resendInvitation } from '../../api/users';

interface ProjectTeamProps {
  projectData: ProjectInterface | 'not-found';
  externalShowAddModal?: boolean;
  externalSetShowAddModal?: (show: boolean) => void;
  onProjectUpdate?: () => Promise<void>;
}

export const ProjectTeam: React.FC<ProjectTeamProps> = ({ 
  projectData, 
  externalShowAddModal, 
  externalSetShowAddModal,
  onProjectUpdate
}) => {
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const isShowAddModal = externalShowAddModal !== undefined ? externalShowAddModal : showAddModal;
  const setIsShowAddModal = externalSetShowAddModal || setShowAddModal;
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showChangeEscalationLevelModal, setShowChangeEscalationLevelModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { users: allUsers } = useUsersStore();
  const { loadUsersPage } = useUsersPage();
  const navigate = useNavigate();
  const { keycloak: { token } } = useKeycloak();
  const { isFeatureEnabled } = useFeatureFlag();
  
  if (projectData === 'not-found') {
    return <div>Project not found</div>;
  }

  const projectId = projectData.id || '';
  const { users, roles, loading, error, addUser, updateUser, removeUser, loadUsers } = useProjectUsers(projectId);
  
  // Escalation matrix store and page hook
  const { levels } = useEscalationMatrixStore();
  const { loadEscalationMatrixLevels } = useEscalationMatrixPage(projectId);
  const escalationLevels = levels[projectId] || [];
  
  // Debug log
  console.log('ProjectTeam: escalationLevels', escalationLevels);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (isUnmounted) return;

    const handleClickOutside = () => {
      if (!isUnmounted) {
      setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown, isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  // Load all users on component mount only if no data exists
  useEffect(() => {
    if (allUsers.length === 0) {
      loadUsersPage(1).catch(error => {
        console.error('Failed to load users page:', error);
      });
    }
  }, []); // Remove dependencies to avoid unnecessary calls

  // Load escalation levels on component mount
  useEffect(() => {
    if (projectId) {
      loadEscalationMatrixLevels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Remove loadEscalationMatrixLevels from dependencies to avoid infinite requests

  const handleAddUser = async (_userId: string, userData: any) => {
    try {
      console.log('ProjectTeam: adding user', userData);
      console.log('ProjectTeam: current users count before:', users?.length);
      // Pass the userData directly since it already contains all required fields
      await addUser('', userData);
      console.log('ProjectTeam: user added successfully');
      
      // Explicitly reload users to ensure fresh data
      await loadUsers();
      console.log('ProjectTeam: current users count after:', users?.length);
      
      // Also trigger project data refresh if callback provided
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('ProjectTeam: failed to add user', error);
      throw error;
    }
  };

  const handleUpdateUserRole = async (userId: string, newRoleId: string) => {
    try {
      const user = users.find(u => u.user_id === userId);
      if (user) {
        // Send data according to new API specification
        const updateData = {
          id: user.id, // Required: record ID
          project_role_id: newRoleId, // Only update the role
          // Include existing data to avoid losing it
          first_name: user.user.first_name,
          last_name: user.user.last_name,
          email: user.user.email,
          phone: (user as any).phone,
          escalation_level_id: user.escalation_level_id,
          department: user.department,
          status: user.status,
          skills: user.skills
        };

        await updateUser(userId, updateData);
        toast.success('User role updated successfully');
        
        // Trigger project data refresh
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleUpdateUserEscalationLevel = async (userId: string, newLevelId: string) => {
    try {
      const user = users.find(u => u.user_id === userId);
      if (user) {
        // Send data according to new API specification
        const updateData = {
          id: user.id, // Required: record ID
          escalation_level_id: newLevelId || '', // Update escalation level (empty string for "Any")
          // Include existing data to avoid losing it
          first_name: user.user.first_name,
          last_name: user.user.last_name,
          email: user.user.email,
          phone: (user as any).phone,
          project_role_id: user.project_role_id,
          department: user.department,
          status: user.status,
          skills: user.skills
        };

        await updateUser(userId, updateData);
        toast.success('Escalation level updated successfully');
        
        // Trigger project data refresh
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error) {
      toast.error('Failed to update escalation level');
    }
  };

  const handleResendInvitation = async (email: string) => {
    if (!token) {
      toast.error('No authentication token');
      return;
    }

    try {
      setResendingInvitation(email);
      await resendInvitation(token, { email });
      toast.success('Invitation resent successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation';
      toast.error(errorMessage);
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleEditUser = (member: any) => {
    setSelectedMember(member);
    setShowEditUserModal(true);
    setOpenDropdown(null);
  };

  const handleUpdateUser = async (userData: any) => {
    try {
      await updateUser(selectedMember.user_id, userData);
      toast.success('User updated successfully');
      
      // Force reload users list to ensure UI is updated
      await loadUsers();
      
      // Also trigger project data refresh
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleRemoveUser = async (member: any) => {
    if (!window.confirm(`Are you sure you want to remove ${member.user.first_name} ${member.user.last_name} from this project?`)) {
      return;
    }

    try {
      await removeUser(member.user_id);
      toast.success('User removed successfully');
      setOpenDropdown(null);
      
      // Trigger project data refresh
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading team members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <p>Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Safety check for users array
  if (!Array.isArray(users)) {
    console.error('ProjectTeam: users is not an array', users);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <p>Invalid data format. Please refresh the page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProjectTeamErrorBoundary>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
          <p className="text-sm text-gray-500">{users.length} member{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button 
          onClick={() => setIsShowAddModal(true)}
          className="cursor-pointer bg-white hover:bg-blue-50 border border-gray-100 text-blue-500 font-bold py-2 px-4 rounded-full"
        >
          + Add Team Member
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avatar
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                First Name
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Name
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Escalation Level
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Join Date
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(users || [])
              .filter((member) => {
                // Filter out users with Developer roles in production
                if (!isFeatureEnabled('SHOW_DEVELOPER_ROLES')) {
                  const memberRole = roles.find(role => role.id === member.project_role_id);
                  if (memberRole?.name?.toLowerCase().includes('developer')) {
                    return false;
                  }
                }
                return true;
              })
              .map((member) => {
              try {
                // Safety check for member data
                if (!member || !member.user) {
                  console.warn('ProjectTeam: invalid member data', member);
                  return null;
                }



                return (
                  <tr 
                    key={member.id || member.user_id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/users/${member.user.id}`)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                        <Avatar
                          firstName={member.user.first_name}
                          lastName={member.user.last_name}
                          email={member.user.email}
                          size={40}
                        />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {member.user.first_name || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {member.user.last_name || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{member.user.email || 'N/A'}</span>
                        {/* Project owner is always considered approved */}
                        {member.user.id === projectData.owner_id ? (
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <>
                            {member.user.email && !member.user.is_email_verified && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                            {member.user.email && member.user.is_email_verified && (
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMember(member);
                            setShowChangeRoleModal(true);
                          }}
                          className="text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          {roles.find(role => role.id === member.project_role_id)?.name || 'No role'}
                        </button>
                        {member.user.id === projectData.owner_id && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Owner
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMember(member);
                          setShowChangeEscalationLevelModal(true);
                        }}
                        className="text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      >
                        {member.escalation_level?.name || 'Any'}
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(member as any).phone || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center justify-end">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === member.user.email ? null : member.user.email);
                            }}
                            className="p-1 rounded-md hover:bg-gray-100 focus:outline-none"
                          >
                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          {openDropdown === member.user.email && (
                            <div className="absolute right-0 z-10 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditUser(member);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit User
                                </button>
                                {!member.user.is_email_verified && member.user.id !== projectData.owner_id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResendInvitation(member.user.email);
                                      setOpenDropdown(null);
                                    }}
                                    disabled={resendingInvitation === member.user.email}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {resendingInvitation === member.user.email ? 'Sending...' : 'Resend Invitation'}
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveUser(member);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Remove User
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              } catch (renderError) {
                console.error('ProjectTeam: error rendering member', member, renderError);
                return null;
              }
            }).filter(Boolean)}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-4">No team members yet</div>
          <button 
            onClick={() => setIsShowAddModal(true)}
            className="cursor-pointer w-full p-2 text-left hover:bg-gray-50 text-gray-500 text-sm border border-gray-200 rounded"
          >
            + Add Team Member
          </button>
        </div>
      )}

      <AddUserModal
        isOpen={isShowAddModal}
        onClose={() => setIsShowAddModal(false)}
        onAddUser={handleAddUser}
        availableUsers={allUsers}
        projectUsers={users.map(u => u.user)}
        roles={roles}
        escalationLevels={escalationLevels}
        projectId={projectId}
        onEscalationLevelsUpdate={loadEscalationMatrixLevels}
      />

      {selectedMember && (
        <ChangeRoleModal
          isOpen={showChangeRoleModal}
          onClose={() => {
            setShowChangeRoleModal(false);
            setSelectedMember(null);
          }}
          onChangeRole={(newRoleId: string) => handleUpdateUserRole(selectedMember.user_id, newRoleId)}
          currentRoleId={selectedMember.project_role_id || ''}
          roles={roles}
        />
      )}

      {selectedMember && (
        <ChangeEscalationLevelModal
          isOpen={showChangeEscalationLevelModal}
          onClose={() => {
            setShowChangeEscalationLevelModal(false);
            setSelectedMember(null);
          }}
          onChangeEscalationLevel={(newLevelId: string) => handleUpdateUserEscalationLevel(selectedMember.user_id, newLevelId)}
          currentLevelId={selectedMember.escalation_level_id || ''}
          levels={escalationLevels}
        />
      )}

      {selectedMember && (
        <EditUserModal
          isOpen={showEditUserModal}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedMember(null);
          }}
          onUpdateUser={handleUpdateUser}
          user={selectedMember}
          roles={roles}
          escalationLevels={escalationLevels}
        />
      )}
    </ProjectTeamErrorBoundary>
  );
}; 